import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial, IconMaterialProps } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import React, { ReactNode, useEffect, useState } from 'react';

import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../common/Utils';
import { useLagoon } from '../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { Constants } from '../../Constants';
import {
  hcbsLandingDrawerIcon,
  landingDrawerIcon,
  landingDrawerIconPhone,
  phoneOnly,
} from '../../ConstantsStyles';
import { ModalContainer } from './ModalContainer';
import {
  FlexContainerList,
  InfoAlignmentContainer,
  infoSubTextContainer,
  infoTitleContainer,
} from './ProviderLocationsDetails.style';

type Props = {
  icon: string;
  infoTitle: string;
  infoSubtext?: null | string;
  children?: ReactNode;
  variant?: 'filled' | 'outlined';
  showSubTitle?: Boolean;
  isFromVirtualProviderGroup?: boolean;
  showPopover?: boolean;
  popoverContent?: string;
  popoverTitle?: string;
  popoverTestId?: string;
  showModal?: boolean;
  iconType?: string;
  contentPopoverContent?: string;
  contentPopoverTitle?: string;
  showContentPopover?: boolean;
  isHcbs?: boolean;
};

const renderHTMLContent = (content: string | undefined, enableHTML: boolean) =>
  enableHTML ? (
    <div
      dangerouslySetInnerHTML={{
        __html: content || '',
      }}
    />
  ) : (
    content
  );

const getTestId = (infoTitle: string) => {
  const infoTitleLowerCase = infoTitle.toLowerCase().split(' ').join('-');
  return infoTitleLowerCase.includes('accepting')
    ? 'accepting-patients-status'
    : infoTitleLowerCase;
};

const PhoneScreenLayout = ({
  infoTitle,
  headingStyle,
  phoneScreen,
  showPopover,
  setIsOpen,
  doShowModal,
  isOpen,
  popoverContent,
  popoverTitle,
}: {
  infoTitle: string;
  headingStyle: any;
  phoneScreen: boolean;
  showPopover: boolean;
  setIsOpen: (open: boolean) => void;
  doShowModal: boolean;
  isOpen: boolean;
  popoverContent?: string;
  popoverTitle?: string;
}) => (
  <div style={{ display: 'inline' }}>
    <Text
      css={{
        ...infoTitleContainer(phoneScreen),
        ...headingStyle,
        display: 'inline',
      }}
      data-testid={`${infoTitle}-Heading`.replace(/ /g, '-')}
    >
      <p style={{ margin: '0px' }}>{infoTitle}</p>
    </Text>
    {showPopover && (
      <IconMaterial
        data-testid="popover-icon"
        icon="info"
        onClick={() => setIsOpen(true)}
        size={24}
        style={{
          display: 'inline',
          marginLeft: '4px',
          verticalAlign: 'middle',
        }}
        variant="outlined"
      />
    )}
    {doShowModal && (
      <ModalContainer
        isOpen={isOpen}
        popoverContent={popoverContent}
        popoverTitle={popoverTitle}
        setIsOpen={setIsOpen}
      />
    )}
  </div>
);

const DesktopScreenLayout = ({
  infoTitle,
  headingStyle,
  phoneScreen,
  showPopover,
  enableColorCodedSpecialties,
  popoverContent,
  popoverTestId,
  popoverTitle,
  isHcbs,
  doShowModal,
  isOpen,
  setIsOpen,
}: {
  infoTitle: string;
  headingStyle: any;
  phoneScreen: boolean;
  showPopover: boolean;
  enableColorCodedSpecialties: boolean;
  popoverContent?: string;
  popoverTestId?: string;
  popoverTitle?: string;
  isHcbs: boolean;
  doShowModal: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) => (
  <Flex css={{ gap: '4px' }}>
    <Text
      css={{ ...infoTitleContainer(phoneScreen), ...headingStyle }}
      data-testid={`${infoTitle}-Heading`.replace(/ /g, '-')}
    >
      <p style={{ margin: '0px' }}>{infoTitle}</p>
    </Text>
    {showPopover && (
      <Popover
        content={renderHTMLContent(popoverContent, enableColorCodedSpecialties)}
        data-auto-testid={`${popoverTestId}`}
        data-testid={`${popoverTestId}`}
        title={popoverTitle}
        width={enableColorCodedSpecialties ? 339 : 220}
      >
        <IconMaterial
          data-testid="popover-icon"
          icon="info"
          size={isHcbs ? 20 : 24}
          variant="outlined"
        />
      </Popover>
    )}
    {doShowModal && (
      <ModalContainer
        isOpen={isOpen}
        popoverContent={popoverContent}
        popoverTitle={popoverTitle}
        setIsOpen={setIsOpen}
      />
    )}
  </Flex>
);

export const InfoProviderContent = ({
  icon,
  infoTitle,
  infoSubtext,
  children,
  variant,
  showSubTitle = true,
  isFromVirtualProviderGroup,
  showPopover = false,
  popoverContent,
  popoverTitle,
  popoverTestId,
  showModal,
  iconType = 'material',
  contentPopoverContent,
  contentPopoverTitle,
  showContentPopover = false,
  isHcbs = false,
}: Props) => {
  const featureFlags = useLagoon('feature-flags')();

  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;
  const [isOpen, setIsOpen] = useState(false);

  const {
    providerLocations: notAvailableOrUnknownText,
    headingStyle,
    descriptionStyle,
  } = useNotAvailableUnknownText();
  const infoSubtextValue = infoSubtext || notAvailableOrUnknownText;

  const doShowModal = showModal && !showPopover;
  const testId = getTestId(infoTitle);

  const enablePlatinumProviderIdentifier: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER_ANALYTICS
  );

  const enableColorCodedSpecialties: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COLOR_CODED_SPECIALTIES
  );

  useEffect(() => {
    if (isOpen && enablePlatinumProviderIdentifier) {
      adobeModalTrackEvent(Constants.ADOBE_TRACKING.PLATINUM_RECOGNITION);
    }
  }, [isOpen]);

  return (
    <FlexContainerList
      data-auto-testid={`provider-info-container-${testId}`}
      data-testid={`provider-info-container-${testId}`}
    >
      {iconType === 'symbol' ? (
        <IconSymbol
          aria-hidden="true"
          color="$primary1"
          icon={icon}
          size={20}
          variant={variant}
        />
      ) : (
        <IconMaterial
          aria-hidden="true"
          color="$primary1"
          icon={icon as IconMaterialProps['icon']}
          size={20}
          variant={variant}
        />
      )}
      <InfoAlignmentContainer>
        <React.Fragment>
          {phoneScreen ? (
            <PhoneScreenLayout
              doShowModal={doShowModal}
              headingStyle={headingStyle}
              infoTitle={infoTitle}
              isOpen={isOpen}
              phoneScreen={phoneScreen}
              popoverContent={popoverContent}
              popoverTitle={popoverTitle}
              setIsOpen={setIsOpen}
              showPopover={showPopover}
            />
          ) : (
            <DesktopScreenLayout
              doShowModal={doShowModal}
              enableColorCodedSpecialties={enableColorCodedSpecialties}
              headingStyle={headingStyle}
              infoTitle={infoTitle}
              isHcbs={isHcbs}
              isOpen={isOpen}
              phoneScreen={phoneScreen}
              popoverContent={popoverContent}
              popoverTestId={popoverTestId}
              popoverTitle={popoverTitle}
              setIsOpen={setIsOpen}
              showPopover={showPopover}
            />
          )}
          {showPopover && (
            <Drawer
              css={isHcbs ? hcbsLandingDrawerIcon : landingDrawerIcon}
              isOpen={isOpen && phoneScreen}
              onClose={() => setIsOpen(false)}
              position="bottom"
              size="200"
              title={popoverTitle}
            >
              {renderHTMLContent(popoverContent, enableColorCodedSpecialties)}
            </Drawer>
          )}
          {showSubTitle && !isFromVirtualProviderGroup
            ? children || (
                <Flex>
                  <Text
                    css={{
                      ...infoSubTextContainer(phoneScreen),
                      ...descriptionStyle,
                    }}
                    data-testid={`${infoTitle}-subtext-provider-locations-tab`.replace(
                      / /g,
                      '-'
                    )}
                  >
                    {infoSubtextValue}
                  </Text>
                  {showContentPopover && infoSubtext ? (
                    <React.Fragment>
                      {phoneScreen ? (
                        <Layout.Group>
                          <IconMaterial
                            css={{ marginLeft: 4 }}
                            data-testid="popover-icon"
                            icon="info"
                            onClick={() => setIsOpen(true)}
                            size={16}
                            variant="outlined"
                          />
                        </Layout.Group>
                      ) : (
                        <Popover
                          content={contentPopoverContent}
                          data-auto-testid={`${popoverTestId}`}
                          data-testid={`${popoverTestId}`}
                          title={contentPopoverTitle}
                          width={326}
                        >
                          <IconMaterial
                            css={{ marginLeft: 4 }}
                            data-testid="popover-icon"
                            icon="info"
                            size={20}
                            variant="outlined"
                          />
                        </Popover>
                      )}
                      <Drawer
                        css={
                          phoneScreen
                            ? landingDrawerIconPhone
                            : landingDrawerIcon
                        }
                        isOpen={isOpen && phoneScreen}
                        onClose={() => setIsOpen(false)}
                        position="bottom"
                        size="300"
                        title={contentPopoverTitle}
                      >
                        {contentPopoverContent}
                      </Drawer>
                    </React.Fragment>
                  ) : null}
                </Flex>
              )
            : null}
        </React.Fragment>
      </InfoAlignmentContainer>
    </FlexContainerList>
  );
};
